import { useEffect, useMemo, useState } from 'react'

const API_BASE = 'https://api.seattle-crime-predictor.com/api/v1'

const CRIME_TYPES = [
  { label: 'violent-crime', title: 'Violent crime' },
  { label: 'property-crime', title: 'Property crime' },
  { label: 'other-crime', title: 'Other crime' },
] as const

const METRICS = [
  { key: 'test_f1_macro', title: 'Test F1 (macro)', color: '#2563eb' },
  { key: 'test_accuracy', title: 'Test accuracy', color: '#16a34a' },
  { key: 'test_precision', title: 'Test precision', color: '#9333ea' },
  { key: 'test_recall', title: 'Test recall', color: '#ea580c' },
] as const

type CrimeTypeLabel = (typeof CRIME_TYPES)[number]['label']
type MetricKey = (typeof METRICS)[number]['key']

type SeriesPoint = {
  jobId: string
  timestamp: string
  value: number
}

type SeriesResponse = {
  crimeType: string
  attribute: string
  points: SeriesPoint[]
}

function formatPct(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return `${(value * 100).toFixed(1)}%`
}

function formatShortDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

function LineChart({
  points,
  color,
  title,
}: {
  points: SeriesPoint[]
  color: string
  title: string
}) {
  const width = 560
  const height = 220
  const pad = { top: 16, right: 16, bottom: 36, left: 44 }
  const innerW = width - pad.left - pad.right
  const innerH = height - pad.top - pad.bottom

  const { path, dots, yTicks, xLabels } = useMemo(() => {
    if (points.length === 0) {
      return { path: '', dots: [] as { x: number; y: number; p: SeriesPoint }[], yTicks: [] as number[], xLabels: [] as { x: number; label: string }[] }
    }
    const values = points.map((p) => p.value)
    let minY = Math.min(...values)
    let maxY = Math.max(...values)
    if (minY === maxY) {
      minY = Math.max(0, minY - 0.05)
      maxY = Math.min(1, maxY + 0.05)
    }
    const span = maxY - minY || 1
    const xAt = (i: number) =>
      points.length === 1 ? pad.left + innerW / 2 : pad.left + (i / (points.length - 1)) * innerW
    const yAt = (v: number) => pad.top + (1 - (v - minY) / span) * innerH

    const d = points
      .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xAt(i).toFixed(1)} ${yAt(p.value).toFixed(1)}`)
      .join(' ')
    const chartDots = points.map((p, i) => ({ x: xAt(i), y: yAt(p.value), p }))
    const ticks = [minY, minY + span / 2, maxY]
    const labelIdx =
      points.length <= 4
        ? points.map((_, i) => i)
        : [0, Math.floor((points.length - 1) / 2), points.length - 1]
    const labels = [...new Set(labelIdx)].map((i) => ({
      x: xAt(i),
      label: formatShortDate(points[i].timestamp),
    }))
    return { path: d, dots: chartDots, yTicks: ticks, xLabels: labels }
  }, [points, innerW, innerH, pad.left, pad.top])

  if (points.length === 0) {
    return (
      <div className="h-56 bg-gray-50 rounded flex items-center justify-center border border-dashed border-gray-200">
        <p className="text-sm text-gray-500">No {title.toLowerCase()} data for this crime type yet</p>
      </div>
    )
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-56" role="img" aria-label={title}>
      {yTicks.map((t) => {
        const values = points.map((p) => p.value)
        let minY = Math.min(...values)
        let maxY = Math.max(...values)
        if (minY === maxY) {
          minY = Math.max(0, minY - 0.05)
          maxY = Math.min(1, maxY + 0.05)
        }
        const span = maxY - minY || 1
        const y = pad.top + (1 - (t - minY) / span) * innerH
        return (
          <g key={t}>
            <line x1={pad.left} x2={width - pad.right} y1={y} y2={y} stroke="#e5e7eb" strokeWidth={1} />
            <text x={pad.left - 8} y={y + 4} textAnchor="end" className="fill-gray-500" fontSize={10}>
              {(t * 100).toFixed(0)}%
            </text>
          </g>
        )
      })}
      <path d={path} fill="none" stroke={color} strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" />
      {dots.map(({ x, y, p }) => (
        <circle key={p.jobId + p.timestamp} cx={x} cy={y} r={3.5} fill={color}>
          <title>{`${formatShortDate(p.timestamp)} · ${formatPct(p.value)}\n${p.jobId}`}</title>
        </circle>
      ))}
      {xLabels.map(({ x, label }) => (
        <text key={`${x}-${label}`} x={x} y={height - 12} textAnchor="middle" className="fill-gray-500" fontSize={10}>
          {label}
        </text>
      ))}
    </svg>
  )
}

function Analytics() {
  const [crimeType, setCrimeType] = useState<CrimeTypeLabel>('violent-crime')
  const [seriesByMetric, setSeriesByMetric] = useState<Partial<Record<MetricKey, SeriesPoint[]>>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const results = await Promise.all(
          METRICS.map(async (metric) => {
            const url = `${API_BASE}/get-model-metric-series?crimeType=${encodeURIComponent(crimeType)}&attribute=${encodeURIComponent(metric.key)}`
            const res = await fetch(url)
            if (!res.ok) {
              throw new Error(`${metric.key} request failed (${res.status})`)
            }
            const data = (await res.json()) as SeriesResponse
            return [metric.key, data.points ?? []] as const
          })
        )
        if (cancelled) return
        const next: Partial<Record<MetricKey, SeriesPoint[]>> = {}
        for (const [key, points] of results) {
          next[key] = points
        }
        setSeriesByMetric(next)
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load analytics')
          setSeriesByMetric({})
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [crimeType])

  const latest = useMemo(() => {
    const pick = (key: MetricKey) => {
      const pts = seriesByMetric[key] ?? []
      return pts.length ? pts[pts.length - 1].value : null
    }
    return {
      f1: pick('test_f1_macro'),
      accuracy: pick('test_accuracy'),
      precision: pick('test_precision'),
      recall: pick('test_recall'),
      jobs: seriesByMetric.test_f1_macro?.length ?? 0,
    }
  }, [seriesByMetric])

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Model performance over jobs</h2>
          <p className="text-sm text-gray-500">
            Chronological test metrics from <code className="text-xs">model_metadata_*.json</code> across Batch jobs
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          Crime type
          <select
            value={crimeType}
            onChange={(e) => setCrimeType(e.target.value as CrimeTypeLabel)}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            {CRIME_TYPES.map((t) => (
              <option key={t.label} value={t.label}>
                {t.title}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900">Latest F1</h3>
          <p className="text-3xl font-bold text-blue-600">{formatPct(latest.f1)}</p>
          <p className="text-sm text-gray-500">test_f1_macro</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900">Latest accuracy</h3>
          <p className="text-3xl font-bold text-green-600">{formatPct(latest.accuracy)}</p>
          <p className="text-sm text-gray-500">test_accuracy</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900">Latest precision</h3>
          <p className="text-3xl font-bold text-purple-600">{formatPct(latest.precision)}</p>
          <p className="text-sm text-gray-500">test_precision</p>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900">Latest recall</h3>
          <p className="text-3xl font-bold text-orange-600">{formatPct(latest.recall)}</p>
          <p className="text-sm text-gray-500">
            {latest.jobs} job{latest.jobs === 1 ? '' : 's'} in series
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      )}
      {loading && (
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
          Loading metric series…
        </div>
      )}

      {!loading && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {METRICS.map((metric) => (
            <div key={metric.key} className="bg-white p-6 rounded-lg shadow">
              <h3 className="text-lg font-medium text-gray-900 mb-2">{metric.title}</h3>
              <p className="text-xs text-gray-500 mb-3">
                {crimeType} · {seriesByMetric[metric.key]?.length ?? 0} points
              </p>
              <LineChart
                points={seriesByMetric[metric.key] ?? []}
                color={metric.color}
                title={metric.title}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Analytics
